/**
 * API Client - Base configuration for all API calls with Supabase Auth support
 */

import { getAccessToken, refreshSession } from '../config/supabase';
import { runWithTimeout } from '../utils/requestTimeout';

// 🔴 SANDBOX IS A SURFACE, NOT A USER. (Board 08-05, Task 6.)
//
// The server mounts the SAME router tree twice (api/app.js):
//   /api/*          -> ALWAYS LIVE. Admin panel / CRM. Real email, real money.
//   /api/sandbox/*  -> the Test Admin Dashboard surface. Side effects suppressed.
//
// The client's only job is to pick which surface it is talking to. It cannot
// forge sandbox state either way: the server derives it from the mount path, so
// a request to /api is live no matter what the client believes, and a request to
// /api/sandbox is sandboxed no matter what the client believes.
//
// THE ADMIN PANEL IS ALWAYS LIVE, even for a test admin. P.: "The admin panel
// itself is LIVE ... if somebody does anything in the admin panel, that is LIVE
// and LEGITIMATE." That is why the /admin check below is unconditional and is
// NOT gated on the sandbox preference.
const LIVE_BASE = '/api';
const SANDBOX_BASE = '/api/sandbox';
const SANDBOX_SURFACE_KEY = 'sandboxSurface';

// Seeded from localStorage so a HARD RELOAD of the test dashboard still targets
// the sandbox surface for the first request, before AuthContext has hydrated.
// This is a preference, never an authority — see the note above.
let sandboxSurface = (() => {
  try {
    return localStorage.getItem(SANDBOX_SURFACE_KEY) === 'true';
  } catch {
    return false;
  }
})();

/** Kept in lockstep with AuthContext's `sandboxMode` (which comes from /auth/me). */
export const setSandboxSurface = (on) => {
  sandboxSurface = on === true;
  try {
    if (sandboxSurface) localStorage.setItem(SANDBOX_SURFACE_KEY, 'true');
    else localStorage.removeItem(SANDBOX_SURFACE_KEY);
  } catch {
    /* private-mode storage failures must not break routing */
  }
};

export const getSandboxSurface = () => sandboxSurface;

// 🔴 THE PREVIEW TIER RIDES ON THE WIRE, NOT ON THE COMPONENT TREE. (Board 08-06, Task 9.)
//
// The Test Admin Dashboard's tier toggle has to move the price on the catalog, the product
// detail page, the intake and the checkout total. Those four surfaces live in THREE different
// layouts (`/catalog` is under DashboardLayout, `/products/:slug` is under PublicLayout, and
// `/checkout` and `/intake/:slug` are standalone), so nothing mounted in a layout can reach all
// of them. The one thing they DO share is this client.
//
// Seeded from localStorage for the same reason as `sandboxSurface`: after a hard reload the very
// first request must already carry the selected tier, before TierContext has hydrated.
const TIER_PREVIEW_KEY = 'demoTier';

let tierPreview = (() => {
  try {
    return sandboxSurface ? localStorage.getItem(TIER_PREVIEW_KEY) : null;
  } catch {
    return null;
  }
})();

/** Kept in lockstep with TierContext's resolved `tierType`. A falsy value clears the preview. */
export const setTierPreview = (tier) => {
  tierPreview = tier || null;
};

/** True while the user is looking at the admin panel, which is always live. */
const onAdminSurface = () =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

// The test-dashboard enter/exit levers are ADMIN-PANEL actions ("The admin panel itself is
// LIVE"), and exit is the way OUT of sandbox — it must never depend on the sandbox surface
// being healthy. Pin them live regardless of which surface the user is currently on.
const ALWAYS_LIVE = /^\/admin\/test-admin(\/|$)/;

const apiBase = (endpoint = '') =>
  sandboxSurface && !onAdminSurface() && !ALWAYS_LIVE.test(endpoint)
    ? SANDBOX_BASE
    : LIVE_BASE;

// Only these two answer `tierType`: productController's `getViewerTierOpts` and
// checkoutController's `resolveUserTierType`, both through the single gate
// `resolveTierPreviewOverride` in api/services/startingAtService.js. Appending it to every
// sandbox request would be a wire that lies about what is being asked.
const TIER_PREVIEW_ENDPOINTS = /^\/(products|checkout)(\/|\?|$)/;

// The param is attached behind the SAME condition that chose the sandbox mount — `apiBase`
// returning SANDBOX_BASE is the whole test. That keeps display and charge on one gate: what the
// product page shows and what checkout writes cannot drift apart, because they are the same
// decision made once. A live or admin-surface request never carries it.
const withTierPreview = (endpoint) => {
  if (!tierPreview) return endpoint;
  if (apiBase(endpoint) !== SANDBOX_BASE) return endpoint;
  if (!TIER_PREVIEW_ENDPOINTS.test(endpoint)) return endpoint;
  const sep = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${sep}tierType=${encodeURIComponent(tierPreview)}`;
};

// `api.get(endpoint, { params })` is a shape callers have always written, but until now the
// options object was read only for `headers` and `params` was silently dropped — the request went
// out with NO query string, the server applied its defaults, and nothing anywhere reported an
// error. Appending here (rather than inside `buildUrl`) keeps the params on the ENDPOINT, so
// `withTierPreview`'s own `?`/`&` separator choice still sees them and cannot emit a second `?`.
// `undefined`/`null` are dropped rather than serialised as the literal strings "undefined"/"null".
const withQueryParams = (endpoint, params) => {
  if (!params) return endpoint;
  const search = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  ).toString();
  if (!search) return endpoint;
  const sep = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${sep}${search}`;
};

const buildUrl = (endpoint) => `${apiBase(endpoint)}${withTierPreview(endpoint)}`;

// Token storage keys
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const TOKEN_EXPIRY_KEY = 'tokenExpiry';

// Get tokens from storage
const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
const getStoredRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);
const getTokenExpiry = () => localStorage.getItem(TOKEN_EXPIRY_KEY);

// Store tokens
export const storeTokens = (token, refreshToken, expiresAt) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (expiresAt) localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());
};

// Clear tokens
export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

// Check if token is expired or about to expire (within 5 minutes)
const isTokenExpired = () => {
  const expiry = getTokenExpiry();
  if (!expiry) return true;

  const expiryTime = new Date(parseInt(expiry, 10) * 1000); // Convert seconds to ms
  const now = new Date();
  const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

  return now.getTime() > (expiryTime.getTime() - bufferTime);
};

// Refresh access token
const refreshAccessToken = async () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  try {
    // Try server-side refresh first
    const response = await fetch(`${apiBase()}/auth/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });

    if (response.ok) {
      const data = await response.json();
      storeTokens(data.token, data.refreshToken, data.expiresAt);
      return data.token;
    }

    // If server refresh fails, try Supabase client refresh
    const session = await refreshSession();
    if (session) {
      storeTokens(session.access_token, session.refresh_token, session.expires_at);
      return session.access_token;
    }

    // Refresh failed, clear tokens
    clearTokens();
    return null;
  } catch {
    clearTokens();
    return null;
  }
};

// Get valid access token (refresh if needed)
const getValidToken = async () => {
  let token = getStoredToken();

  if (!token) {
    // Try to get from Supabase session
    token = await getAccessToken();
    if (token) {
      const session = await refreshSession();
      if (session) {
        storeTokens(session.access_token, session.refresh_token, session.expires_at);
      }
    }
    return token;
  }

  // Check if token needs refresh
  if (isTokenExpired()) {
    token = await refreshAccessToken();
  }

  return token;
};

// Build headers
const buildHeaders = async (customHeaders = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...customHeaders
  };

  const token = await getValidToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// Handle response
const handleResponse = async (response) => {
  // Handle empty responses
  const text = await response.text();
  let data = {};

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      // If response is not JSON, create error object
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      return { message: text };
    }
  }

  if (!response.ok) {
    const error = new Error(data.error || data.message || 'Request failed');
    error.status = response.status;
    error.code = data.code;
    error.errors = data.errors;
    // Structured error bodies carry more than error/code — the invite conflict
    // 409, for example, describes the existing account (existingUserId,
    // displayName, inviteStatus) so the UI can offer the right recovery. Keep
    // the whole body; picking fields here silently strips what callers need.
    error.data = data;

    // If unauthorized, clear tokens
    if (response.status === 401) {
      clearTokens();
    }

    throw error;
  }

  return data;
};

// Retry with token refresh on 401
const fetchWithRetry = async (url, options, retried = false) => {
  const response = await fetch(url, options);

  // If unauthorized and haven't retried, try refreshing token
  if (response.status === 401 && !retried) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      // Retry with new token
      const newHeaders = {
        ...options.headers,
        'Authorization': `Bearer ${newToken}`
      };
      return fetch(url, { ...options, headers: newHeaders });
    }
  }

  return response;
};

// API methods
export const api = {
  get: async (endpoint, options = {}) => {
    const headers = await buildHeaders(options.headers);
    const response = await fetchWithRetry(buildUrl(withQueryParams(endpoint, options.params)), {
      method: 'GET',
      headers,
      ...(options.signal ? { signal: options.signal } : {})
    });
    return handleResponse(response);
  },

  post: async (endpoint, body, options = {}) => {
    const headers = await buildHeaders(options.headers);
    // Card #54415 (D3). `options.timeoutMs` bounds the whole request, retry included, and the
    // abort reaches `fetch` itself. Calls that pass no budget keep the previous behaviour
    // byte-for-byte — this is opt-in per call site, not a global change. ROLLBACK: delete the
    // `runWithTimeout` wrapper and inline `fetchWithRetry(...)` again.
    const response = await runWithTimeout(
      (signal) => fetchWithRetry(buildUrl(endpoint), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        ...(signal ? { signal } : {})
      }),
      { timeoutMs: options.timeoutMs, signal: options.signal }
    );
    return handleResponse(response);
  },

  put: async (endpoint, body, options = {}) => {
    const headers = await buildHeaders(options.headers);
    const response = await fetchWithRetry(buildUrl(endpoint), {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    });
    return handleResponse(response);
  },

  patch: async (endpoint, body, options = {}) => {
    const headers = await buildHeaders(options.headers);
    const response = await fetchWithRetry(buildUrl(endpoint), {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body)
    });
    return handleResponse(response);
  },

  delete: async (endpoint, options = {}) => {
    const headers = await buildHeaders(options.headers);
    const response = await fetchWithRetry(buildUrl(endpoint), {
      method: 'DELETE',
      headers
    });
    return handleResponse(response);
  },

  // For file uploads
  upload: async (endpoint, formData, _options = {}) => {
    const token = await getValidToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetchWithRetry(buildUrl(endpoint), {
      method: 'POST',
      headers,
      body: formData
    });
    return handleResponse(response);
  }
};

// 🔴 ADMIN REVENUE CALCULATION — the five READ-ONLY economics GETs (cards 1239-1245).
//
// Wrapped here rather than hand-rolled at the call sites so every request inherits this
// client's auth, token refresh and surface routing. All five are GETs by construction:
// the Revenue Calculation page renders money, it never writes a price, a cost or an order.
//
// Empty/null params are DROPPED rather than sent as the string "null" — an empty filter
// must mean "unfiltered", and `?providerType=null` would be read by the server as a real
// provider named "null" and silently return zero rows.
const toRevenueQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

export const revenueCalculationApi = {
  getSummary: (params) => api.get(`/admin/revenue-calculation/summary${toRevenueQuery(params)}`),
  getProducts: (params) => api.get(`/admin/revenue-calculation/products${toRevenueQuery(params)}`),
  getProductTimeline: (productId, params) =>
    api.get(`/admin/revenue-calculation/products/${productId}/timeline${toRevenueQuery(params)}`),
  getBlockers: () => api.get('/admin/revenue-calculation/blockers'),
  getAssumptions: () => api.get('/admin/revenue-calculation/assumptions'),
  getReconciliation: (params) =>
    api.get(`/admin/revenue-calculation/reconciliation${toRevenueQuery(params)}`)
};

export default api;
